import { agentClient } from './agent-client'

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export enum ErrorType {
  NO_SEQUENCE = 'NO_SEQUENCE',
  CAISSE_LOCKED = 'CAISSE_LOCKED',
  CAISSE_NOT_RUNNING = 'CAISSE_NOT_RUNNING',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INSUFFICIENT_STOCK = 'INSUFFICIENT_STOCK',
  UNKNOWN = 'UNKNOWN',
}

// ---------------------------------------------------------------------------
// Execution result
// ---------------------------------------------------------------------------

export interface ExecutionResult {
  success: boolean
  error?: string
  errorCode?: string
  actionRequired?: string
  transaction_id?: string
}

// ---------------------------------------------------------------------------
// Retry Manager
// ---------------------------------------------------------------------------

class RetryManager {
  private maxRetries = 2
  private retryDelay = 3000

  /**
   * Classify an error from an execution result into a known ErrorType.
   */
  classifyError(result: ExecutionResult): ErrorType {
    const code = (result.errorCode ?? '').toUpperCase()
    const msg = (result.error ?? '').toUpperCase()

    if (code.includes('NO_SEQUENCE') || code.includes('PRODUCT_NOT_FOUND')) {
      return ErrorType.NO_SEQUENCE
    }
    if (code.includes('CAISSE_LOCKED') || msg.includes('CAISSE_LOCKED')) {
      return ErrorType.CAISSE_LOCKED
    }
    if (code.includes('CAISSE_NOT_RUNNING') || msg.includes('CAISSE_NOT_RUNNING')) {
      return ErrorType.CAISSE_NOT_RUNNING
    }
    if (code.includes('INSUFFICIENT_STOCK') || msg.includes('INSUFFICIENT_STOCK')) {
      return ErrorType.INSUFFICIENT_STOCK
    }

    // Network-level errors (fetch failures, timeouts, abort)
    if (
      msg.includes('NETWORK') ||
      msg.includes('TIMEOUT') ||
      msg.includes('ABORT') ||
      msg.includes('FETCH') ||
      msg.includes('FAILED TO FETCH')
    ) {
      return ErrorType.NETWORK_ERROR
    }

    return ErrorType.UNKNOWN
  }

  /**
   * Execute an operation with intelligent retry logic.
   *
   * - NO_SEQUENCE: blocks immediately, no retry (product misconfigured)
   * - CAISSE_LOCKED: tries unlock then retries
   * - NETWORK_ERROR / UNKNOWN: retries with delay
   * - BLOCK_TERMINAL action: blocks immediately
   */
  async executeWithRetry(
    operation: () => Promise<ExecutionResult>,
    onBlock: (reason: string) => void,
  ): Promise<ExecutionResult> {
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      let result: ExecutionResult

      try {
        result = await operation()
      } catch (err) {
        // Network-level failure (fetch threw)
        const message = err instanceof Error ? err.message : String(err)
        result = {
          success: false,
          error: message,
          errorCode: 'NETWORK_ERROR',
        }
      }

      // Success
      if (result.success) {
        return result
      }

      // Server explicitly requests terminal block
      if (result.actionRequired === 'BLOCK_TERMINAL') {
        onBlock(result.error ?? 'Borne bloquee par le serveur')
        return result
      }

      // Classify the error
      const errorType = this.classifyError(result)

      switch (errorType) {
        case ErrorType.NO_SEQUENCE:
          // No retry — product is misconfigured
          onBlock('Produit sans sequence')
          return result

        case ErrorType.INSUFFICIENT_STOCK:
          // No retry — stock is empty
          onBlock(result.error ?? 'Stock insuffisant')
          return result

        case ErrorType.CAISSE_NOT_RUNNING:
          onBlock('Caisse non demarree')
          return result

        case ErrorType.CAISSE_LOCKED: {
          // Try to unlock, then retry
          const unlocked = await this.tryUnlockCaisse()
          if (!unlocked && attempt >= this.maxRetries) {
            onBlock('Caisse verrouillee, impossible de deverrouiller')
            return result
          }
          // If unlocked, fall through to retry
          break
        }

        case ErrorType.NETWORK_ERROR:
        case ErrorType.UNKNOWN:
          // Retry with delay if attempts remain
          if (attempt < this.maxRetries) {
            await this.delay(this.retryDelay)
          }
          break
      }
    }

    // All retries exhausted
    onBlock('Echec apres plusieurs tentatives')
    return { success: false, error: 'Max retries exceeded' }
  }

  /**
   * Attempt to unlock the caisse via the agent.
   */
  private async tryUnlockCaisse(): Promise<boolean> {
    try {
      const ok = await agentClient.executeUnlockSequence()
      if (ok) {
        // Wait for caisse to stabilize after unlock
        await this.delay(2000)
      }
      return ok
    } catch {
      return false
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const retryManager = new RetryManager()
